/**
 * MFD Renderer - Multi-Function Displays
 * Renders Left MFD (TADS/Video) and Right MFD (TSD/Radar)
 */

import * as THREE from 'three';
import type { PhysicsState } from '../systems/HelicopterPhysics';
import type { Target } from '../systems/WeaponSystem';

export class MFDRenderer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private readonly mfdColor = '#00FF40'; // Phosphor green
  private readonly mfdSize = 200;
  private readonly mfdMargin = 20;
  private radarWiperAngle = 0;
  private tadsMode: 'DVO' | 'FLIR' = 'DVO';

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to get 2D canvas context for MFD renderer');
    }
    this.ctx = ctx;
  }

  /**
   * Set up TADS camera for left MFD
   */
  setupTADSCamera(_scene: THREE.Scene): void {
    // Reserved for future TADS camera implementation
  }

  /**
   * Render both MFDs
   */
  render(
    state: Readonly<PhysicsState>,
    targets: Target[],
    helicopterRotation: { pitch: number; roll: number; yaw: number }
  ): void {
    // Left MFD - TADS/Video
    this.renderLeftMFD(state, helicopterRotation);

    // Right MFD - TSD/Radar
    this.renderRightMFD(state, targets);
  }

  private renderLeftMFD(
    _state: Readonly<PhysicsState>,
    rotation: { pitch: number; roll: number; yaw: number }
  ): void {
    const { height } = this.canvas;
    const x = this.mfdMargin;
    const y = height - this.mfdSize - this.mfdMargin;

    // Draw MFD border
    this.ctx.strokeStyle = this.mfdColor;
    this.ctx.lineWidth = 2;
    this.ctx.strokeRect(x, y, this.mfdSize, this.mfdSize);

    // Title
    this.ctx.fillStyle = this.mfdColor;
    this.ctx.font = '14px "Courier New", monospace';
    this.ctx.textAlign = 'left';
    this.ctx.fillText('TADS', x + 5, y - 5);
    this.ctx.fillText(this.tadsMode, x + this.mfdSize - 40, y - 5);

    // Simulate TADS view with crosshairs and targeting info
    this.ctx.save();
    this.ctx.translate(x, y);

    // Background (dark for video feed simulation)
    this.ctx.fillStyle = this.tadsMode === 'FLIR' ? '#001a00' : '#0a0a0a';
    this.ctx.fillRect(0, 0, this.mfdSize, this.mfdSize);

    // Crosshairs
    this.ctx.strokeStyle = this.mfdColor;
    this.ctx.lineWidth = 1;
    const centerX = this.mfdSize / 2;
    const centerY = this.mfdSize / 2;

    // Center crosshair
    this.ctx.beginPath();
    this.ctx.arc(centerX, centerY, 20, 0, Math.PI * 2);
    this.ctx.stroke();

    this.ctx.beginPath();
    this.ctx.moveTo(centerX - 30, centerY);
    this.ctx.lineTo(centerX - 10, centerY);
    this.ctx.moveTo(centerX + 10, centerY);
    this.ctx.lineTo(centerX + 30, centerY);
    this.ctx.moveTo(centerX, centerY - 30);
    this.ctx.lineTo(centerX, centerY - 10);
    this.ctx.moveTo(centerX, centerY + 10);
    this.ctx.lineTo(centerX, centerY + 30);
    this.ctx.stroke();

    // Range and azimuth info
    this.ctx.fillStyle = this.mfdColor;
    this.ctx.font = '10px "Courier New", monospace';
    this.ctx.textAlign = 'left';
    this.ctx.fillText('RNG: 500M', 5, 15);
    this.ctx.fillText('AZ: ' + Math.floor((rotation.yaw * 180 / Math.PI + 360) % 360).toString().padStart(3, '0'), 5, 30);
    
    // Zoom indicator
    this.ctx.textAlign = 'right';
    this.ctx.fillText('ZOOM: 1X', this.mfdSize - 5, 15);

    // Simulated scan lines for CRT effect
    for (let i = 0; i < this.mfdSize; i += 4) {
      this.ctx.strokeStyle = 'rgba(0, 255, 64, 0.1)';
      this.ctx.beginPath();
      this.ctx.moveTo(0, i);
      this.ctx.lineTo(this.mfdSize, i);
      this.ctx.stroke();
    }

    this.ctx.restore();
  }

  private renderRightMFD(
    state: Readonly<PhysicsState>,
    targets: Target[]
  ): void {
    const { width, height } = this.canvas;
    const x = width - this.mfdSize - this.mfdMargin;
    const y = height - this.mfdSize - this.mfdMargin;

    // Draw MFD border
    this.ctx.strokeStyle = this.mfdColor;
    this.ctx.lineWidth = 2;
    this.ctx.strokeRect(x, y, this.mfdSize, this.mfdSize);

    // Title
    this.ctx.fillStyle = this.mfdColor;
    this.ctx.font = '14px "Courier New", monospace';
    this.ctx.textAlign = 'left';
    this.ctx.fillText('TSD', x + 5, y - 5);
    this.ctx.fillText('RADAR', x + this.mfdSize - 50, y - 5);

    // Tactical Situation Display
    this.ctx.save();
    this.ctx.translate(x, y);

    // Background
    this.ctx.fillStyle = '#001a00';
    this.ctx.fillRect(0, 0, this.mfdSize, this.mfdSize);

    const centerX = this.mfdSize / 2;
    const centerY = this.mfdSize / 2;
    const scale = 0.15; // Map scale

    // Grid
    this.ctx.strokeStyle = 'rgba(0, 255, 64, 0.3)';
    this.ctx.lineWidth = 1;
    for (let i = -2; i <= 2; i++) {
      // Vertical lines
      this.ctx.beginPath();
      this.ctx.moveTo(centerX + i * 40, 0);
      this.ctx.lineTo(centerX + i * 40, this.mfdSize);
      this.ctx.stroke();
      
      // Horizontal lines
      this.ctx.beginPath();
      this.ctx.moveTo(0, centerY + i * 40);
      this.ctx.lineTo(this.mfdSize, centerY + i * 40);
      this.ctx.stroke();
    }

    // Range rings
    this.ctx.strokeStyle = 'rgba(0, 255, 64, 0.4)';
    for (let r = 40; r <= 100; r += 40) {
      this.ctx.beginPath();
      this.ctx.arc(centerX, centerY, r, 0, Math.PI * 2);
      this.ctx.stroke();
    }

    // Radar wiper (sweeping line)
    this.radarWiperAngle += 0.02;
    if (this.radarWiperAngle > Math.PI * 2) {
      this.radarWiperAngle = 0;
    }

    this.ctx.strokeStyle = 'rgba(0, 255, 64, 0.6)';
    this.ctx.lineWidth = 2;
    this.ctx.beginPath();
    this.ctx.moveTo(centerX, centerY);
    const wiperRange = 100;
    this.ctx.lineTo(
      centerX + Math.cos(this.radarWiperAngle) * wiperRange,
      centerY + Math.sin(this.radarWiperAngle) * wiperRange
    );
    this.ctx.stroke();

    // Player helicopter (triangle)
    this.ctx.fillStyle = this.mfdColor;
    this.ctx.strokeStyle = this.mfdColor;
    this.ctx.lineWidth = 2;
    this.ctx.save();
    this.ctx.translate(centerX, centerY);
    this.ctx.rotate(-state.rotation.yaw); // Rotate to heading
    this.ctx.beginPath();
    this.ctx.moveTo(0, -8);
    this.ctx.lineTo(-5, 5);
    this.ctx.lineTo(5, 5);
    this.ctx.closePath();
    this.ctx.fill();
    this.ctx.stroke();
    this.ctx.restore();

    // Draw targets (squares)
    targets.forEach((target) => {
      const relX = (target.position.x - state.position.x) * scale;
      const relZ = (target.position.z - state.position.z) * scale;
      
      const targetX = centerX + relX;
      const targetZ = centerY + relZ;

      // Only show if within radar range and wiper has swept over
      const angleToTarget = Math.atan2(relZ, relX);
      
      // Normalize angle difference to handle wraparound
      let wiperDiff = Math.abs(angleToTarget - this.radarWiperAngle);
      if (wiperDiff > Math.PI) {
        wiperDiff = Math.PI * 2 - wiperDiff;
      }
      
      const inSweep = wiperDiff < 0.3;

      if (inSweep && Math.abs(relX) < 150 && Math.abs(relZ) < 150) {
        this.ctx.strokeStyle = target.designated ? '#FFB000' : this.mfdColor;
        this.ctx.lineWidth = 2;
        this.ctx.strokeRect(targetX - 4, targetZ - 4, 8, 8);
        
        // Target ID
        this.ctx.fillStyle = this.mfdColor;
        this.ctx.font = '8px "Courier New", monospace';
        this.ctx.textAlign = 'center';
        this.ctx.fillText(target.id.slice(-1), targetX, targetZ - 8);
      }
    });

    // Scale indicator
    this.ctx.fillStyle = this.mfdColor;
    this.ctx.font = '10px "Courier New", monospace';
    this.ctx.textAlign = 'left';
    this.ctx.fillText('SCALE: 1KM', 5, this.mfdSize - 5);

    // Heading
    this.ctx.textAlign = 'right';
    const heading = Math.floor((state.rotation.yaw * 180 / Math.PI + 360) % 360);
    this.ctx.fillText('HDG: ' + heading.toString().padStart(3, '0'), this.mfdSize - 5, this.mfdSize - 5);

    // Scan lines
    for (let i = 0; i < this.mfdSize; i += 4) {
      this.ctx.strokeStyle = 'rgba(0, 255, 64, 0.1)';
      this.ctx.beginPath();
      this.ctx.moveTo(0, i);
      this.ctx.lineTo(this.mfdSize, i);
      this.ctx.stroke();
    }

    this.ctx.restore();
  }

  /**
   * Toggle TADS mode
   */
  toggleTADSMode(): void {
    this.tadsMode = this.tadsMode === 'DVO' ? 'FLIR' : 'DVO';
  }

  /**
   * Get current TADS mode
   */
  getTADSMode(): 'DVO' | 'FLIR' {
    return this.tadsMode;
  }

  resize(_width: number, _height: number): void {
    // MFDs are fixed size, no resize needed
  }

  dispose(): void {
    // Cleanup if needed
  }
}
